"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { ArrowUpRight, BookOpen, ClipboardList, FileText, LifeBuoy, MessagesSquare, Wrench } from "lucide-react";
import { IconSparkles } from "@/components/ui/icons";
import { Z } from "@/lib/z";
import { onColor } from "@/lib/ink";
import { TeamPanel } from "@/components/team-panel";
import { api } from "@/lib/api";
import type { ChatUser } from "@/lib/types";
import { Button } from "@/components/ui/button";

export type FabAction = "new-chat" | "new-task";
export type NavTarget = { path: string; label: string };

type Corner = "tl" | "tr" | "bl" | "br";
type Tab = "chat" | "tasks" | "requests" | "ai";
const STORAGE_KEY = "yuc.devReqFab.corner";
const MARGIN = 20;
const SIZE = 52;
//: На мобильном кнопка меньше и в покое приглушена — она висит поверх контента и
//: на 390px закрывала строку разрыва под карточкой («не хватает 445 тыс ₽ до
//: плана»). Решение Максима 19.08.2026: уменьшить И приглушить. Полностью прятать
//: не стали: она же перетаскивается, и исчезающая кнопка сбивает с толку.
const SIZE_MOBILE = 44;
//: Прозрачность в покое. Не ниже — иначе кнопка теряется на светлом фоне и её
//: перестают находить; выше — контент под ней всё ещё не читается.
const IDLE_OPACITY = 0.55;
//: Когда есть непрочитанное — приглушаем слабее: бейдж должен оставаться заметным.
const IDLE_OPACITY_UNREAD = 0.8;
// Цвета источников непрочитанного (совпадают с неоновыми вкладками панели).
const SRC_COLOR: Record<"chat" | "tasks" | "requests", string> = {
  chat: "#1f9bff", tasks: "#b14bff", requests: "#39e23f",
};

/**
 * Плавающий кружок-FAB «Запросы/задачник» (в перспективе — внутренний чат).
 * Перетаскивается мышью/тачем, на отпускании прилипает к ближайшему углу экрана.
 * Угол запоминается в localStorage. Клик (без перетаскивания) открывает панель.
 */
export function DevRequestFab({ isAdmin = false, currentUserId = "", currentLocation = "", navTargets = [] }: { isAdmin?: boolean; currentUserId?: string; currentLocation?: string; navTargets?: NavTarget[] }) {
  const router = useRouter();
  const [corner, setCorner] = useState<Corner>("br");
  const [open, setOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<Tab>("chat");
  /** Готовый вопрос ассистенту, если панель открыли «спросить про эту цифру». */
  const [aiPrompt, setAiPrompt] = useState<string | null>(null);
  const [initialChatUserId, setInitialChatUserId] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);            // веер быстрых действий
  const [isMobile, setIsMobile] = useState(false);
  //: «Кнопку трогают» — палец/курсор на ней, тянут или открыт веер. В эти моменты
  //: она снова плотная: приглушение нужно, только пока она просто висит.
  const [touched, setTouched] = useState(false);
  const [initialAction, setInitialAction] = useState<FabAction | null>(null);
  const lpTimer = useRef<number | null>(null);                // таймер long-press
  const longPressed = useRef(false);
  const [unread, setUnread] = useState(0);
  const [counts, setCounts] = useState({ chat: 0, tasks: 0, requests: 0 });  // разбивка бейджа
  const [toast, setToast] = useState<{ msg: string; tab: Tab } | null>(null);
  const prevUnread = useRef<{ chat: number; tasks: number; requests: number } | null>(null);
  const moved = useRef(false);
  const startRef = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    setMounted(true);
    const saved = (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY)) as Corner | null;
    if (saved && ["tl", "tr", "bl", "br"].includes(saved)) setCorner(saved);
  }, []);

  // Порог тот же, что у отдела: <1024px app-shell уже мобильный.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Открытие ИИ-чата из меню профиля (админам): сразу вкладка AI.
  // detail.prompt — готовый вопрос: так «Аналитик» спрашивает ассистента про
  // конкретную цифру, не заставляя человека пересказывать её руками.
  useEffect(() => {
    const openAi = (e: Event) => {
      const prompt = (e as CustomEvent<{ prompt?: string }>).detail?.prompt || null;
      setAiPrompt(prompt);
      setInitialTab("ai");
      setOpen(true);
    };
    window.addEventListener("yuc:open-assistant", openAi);
    return () => window.removeEventListener("yuc:open-assistant", openAi);
  }, []);

  // Кнопка «Чат» в панели строки заголовка открывает ЭТУ ЖЕ панель, на вкладке
  // чата. Второй копии переписки нет — панель одна, у неё просто появился
  // второй вход (Максим, 28.08.2026: контролы переехали в шапку).
  useEffect(() => {
    const openChat = () => { setInitialTab("chat"); setOpen(true); };
    // ⚠️ ПОИСК ЖИВЁТ В ШАПКЕ (components/portal-search), а панель — здесь.
    // Строка поиска просит открыть нужную вкладку событием: второй копии
    // панели в портале нет и быть не должно.
    const fabAction = (e: Event) => {
      const d = (e as CustomEvent<{ tab?: string; action?: string | null; userId?: string }>).detail || {};
      if (d.userId) { openChatWith(d.userId); return; }
      runAction((d.tab as Tab) || "chat", (d.action as FabAction | null) ?? null);
    };
    window.addEventListener("yuc:open-chat", openChat);
    window.addEventListener("yuc:fab-action", fabAction);
    return () => {
      window.removeEventListener("yuc:open-chat", openChat);
      window.removeEventListener("yuc:fab-action", fabAction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // бейдж непрочитанного (чат + назначенные задачи) + тост-уведомление при росте
  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const u = await api.teamUnread();
        if (!alive) return;
        const c = { chat: u.chat || 0, tasks: u.tasks || 0, requests: u.requests || 0 };
        setUnread(c.chat + c.tasks + c.requests);
        setCounts(c);
        // Счётчик рисует значок чата в верхней панели — кружка, на котором он
        // висел, больше нет. Опрос остаётся ОДИН, здесь: верхняя панель только
        // слушает событие и не ходит на сервер сама.
        window.dispatchEvent(new CustomEvent("yuc:unread", { detail: c }));
        const prev = prevUnread.current;
        if (prev) {  // не уведомляем на первой загрузке
          if (c.chat > prev.chat) setToast({ msg: "💬 Новое сообщение", tab: "chat" });
          else if (c.tasks > prev.tasks) setToast({ msg: "📋 Новая задача", tab: "tasks" });
          else if (c.requests > prev.requests) setToast({ msg: "🔧 Новая доработка", tab: "requests" });
        }
        prevUnread.current = c;
      } catch { /* ignore */ }
    }
    void tick();
    const id = setInterval(() => void tick(), open ? 15000 : 12000);
    return () => { alive = false; clearInterval(id); };
  }, [open]);

  // автоскрытие тоста
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(id);
  }, [toast]);

  function cornerStyle(c: Corner): React.CSSProperties {
    const v: React.CSSProperties = { position: "fixed", zIndex: Z.FAB };
    if (c === "tl") { v.top = MARGIN; v.left = MARGIN; }
    if (c === "tr") { v.top = MARGIN; v.right = MARGIN; }
    if (c === "bl") { v.bottom = MARGIN; v.left = MARGIN; }
    if (c === "br") { v.bottom = MARGIN; v.right = MARGIN; }
    return v;
  }

  function clearLp() {
    if (lpTimer.current) { window.clearTimeout(lpTimer.current); lpTimer.current = null; }
  }
  function onPointerDown(e: React.PointerEvent) {
    moved.current = false;
    longPressed.current = false;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    startRef.current = { px: e.clientX, py: e.clientY, ox: e.clientX - rect.left, oy: e.clientY - rect.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    // долгое удержание без движения → веер быстрых действий
    clearLp();
    lpTimer.current = window.setTimeout(() => { longPressed.current = true; setMenuOpen(true); }, 480);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!startRef.current) return;
    const dx = e.clientX - startRef.current.px;
    const dy = e.clientY - startRef.current.py;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) { moved.current = true; clearLp(); }  // это перетаскивание, не long-press
    if (moved.current) setDrag({ x: e.clientX - startRef.current.ox, y: e.clientY - startRef.current.oy });
  }
  function onPointerUp(e: React.PointerEvent) {
    clearLp();
    const wasDrag = moved.current;
    if (wasDrag && startRef.current) {
      const cx = e.clientX;
      const cy = e.clientY;
      const right = cx > window.innerWidth / 2;
      const bottom = cy > window.innerHeight / 2;
      const next: Corner = `${bottom ? "b" : "t"}${right ? "r" : "l"}` as Corner;
      setCorner(next);
      try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    }
    startRef.current = null;
    setDrag(null);
    if (!wasDrag && !longPressed.current) {
      // клик без перетаскивания → открыть панель на последней активной вкладке
      let last: string | null = null;
      try { last = window.localStorage.getItem("yuc-fab-tab"); } catch { /* ignore */ }
      setInitialTab((last as Tab) || "chat");
      setInitialAction(null);
      setInitialChatUserId(null);
      setOpen(true);
    }
    longPressed.current = false;
  }

  // Клик по действию веера / палитры → открыть панель на нужной вкладке (+ авто-действие).
  function runAction(tab: Tab, action: FabAction | null) {
    setMenuOpen(false);
    setInitialTab(tab);
    setInitialAction(action);
    setInitialChatUserId(null);
    setOpen(true);
  }
  // Из палитры «Написать <коллега>» — открыть чат сразу с этим человеком.
  function openChatWith(userId: string) {
    setInitialTab("chat");
    setInitialChatUserId(userId);
    setInitialAction(null);
    setOpen(true);
  }


  if (!mounted || typeof document === "undefined") return null;

  const style: React.CSSProperties = drag
    ? { position: "fixed", zIndex: Z.FAB, left: drag.x, top: drag.y }
    : cornerStyle(corner);

  // Цвет бейджа = источник, где больше всего непрочитанного (а не всегда красный).
  const dominant = (["chat", "tasks", "requests"] as const).reduce(
    (a, b) => (counts[b] > counts[a] ? b : a), "chat" as "chat" | "tasks" | "requests");
  const badgeColor = unread > 0 ? SRC_COLOR[dominant] : "var(--danger)";

  return createPortal(
    <>
      {/* ⚠️ КРУЖКА В УГЛУ БОЛЬШЕ НЕТ (Максим, 04.09.2026: «я бы вообще убрал этот
          кружок»). Компонент остался: он держит саму панель, слушает событие
          «yuc:open-chat» от значка чата в верхней панели, считает непрочитанное
          и показывает всплывающие уведомления. Счётчик уехал на тот же значок —
          иначе новые сообщения стало бы не видно. Веер действий («Спросить AI»,
          «Новая задача», «Написать коллеге») ушёл вместе с кружком: ассистент
          есть строкой в чате, задача — в «Быстрых действиях» кабинета,
          коллеги — в списке чатов. */}
      {/* Веер действий убран вместе с кружком: открывался только долгим
          нажатием на него, других входов не было. */}
      {toast && !open && (
        <Button onClick={() => { setInitialTab(toast.tab); setToast(null); setOpen(true); }}
          style={{ ...cornerStyle(corner), zIndex: Z.FAB - 1, [corner.includes("b") ? "marginBottom" : "marginTop"]: SIZE + 12 } as React.CSSProperties}>
          {toast.msg}
        </Button>
      )}
      {open && <TeamPanel onClose={() => { setOpen(false); setInitialAction(null); setInitialChatUserId(null); setAiPrompt(null); }} initialAiPrompt={aiPrompt} isAdmin={isAdmin} corner={corner} currentUserId={currentUserId} initialTab={initialTab} currentLocation={currentLocation} initialAction={initialAction} initialChatUserId={initialChatUserId} />}
    </>,
    document.body,
  );
}
